import { BetrokkenTrein, PatchBetrokkenTrein, useDeleteBetrokkenTrein, usePatchBetrokkenTrein } from "@/entities/betrokken-treinen";
import { useGetContactpersonenForTrein } from "@/entities/contactpersoon";
import {
    Button,
    ConfirmDialog,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    FormFieldInput,
    Helptext,
    SelectFormField,
} from "@/shared/components";
import { optionalNumberString } from "@/shared/utils/zod-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { createMaterieel, getMaterieelTypeOptions } from "../shared/utils";

const editTrainSchema = z
    .object({
        materieelAanduiding: z.string().trim().optional(),
        materieelAantalBakken: optionalNumberString(),
        aantalReizigers: optionalNumberString(),
    })
    .refine(
        (data) => {
            const hasAanduiding = !!data.materieelAanduiding;
            const hasAantalBakken = !!data.materieelAantalBakken;

            // Both must be filled or both must be empty
            return hasAanduiding === hasAantalBakken;
        },
        {
            message: "Beide velden (materieeltype en aantal bakken) moeten ingevuld zijn of beide leeg gelaten worden",
            path: ["materieelAanduiding"],
        }
    );

const createPatchBetrokkenTreinPayload = (
    { materieelAanduiding, materieelAantalBakken, aantalReizigers }: EditTreinFormData,
    dossierId: string,
    treinId: string
): PatchBetrokkenTrein => {
    const materieel = createMaterieel(materieelAanduiding, materieelAantalBakken);

    return {
        dossierId,
        treinId,
        isRelevant: true,
        ...(aantalReizigers && { aantalReizigers: Number(aantalReizigers) }), // potentially available when editing a train with type Handmatig
        ...(materieel && { materieel }),
    };
};

type EditTreinFormData = z.infer<typeof editTrainSchema>;

type EditBetrokkenTreinDialogProps = {
    dossierNummer: string;
    trein: BetrokkenTrein;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

function EditBetrokkenTreinDialog({ dossierNummer, trein, open, onOpenChange }: EditBetrokkenTreinDialogProps) {
    const { data: contactpersonen, isPending, error } = useGetContactpersonenForTrein(dossierNummer, trein.treinNummer);

    const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
    const form = useForm<EditTreinFormData>({
        resolver: zodResolver(editTrainSchema),
        defaultValues: {
            materieelAanduiding: trein.materieel?.aanduiding ?? "",
            materieelAantalBakken: trein.materieel?.aantalBakken ? String(trein.materieel?.aantalBakken) : "",
            aantalReizigers: trein.aantalReizigers ? String(trein.aantalReizigers) : "",
        },
    });

    const patchTrainMutation = usePatchBetrokkenTrein();
    const deleteTrainMutation = useDeleteBetrokkenTrein();

    if (isPending) return null;

    if (error) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContainer>
                    <DialogHeader>
                        <DialogTitle>Trein {BetrokkenTrein.formatTrainLabel(trein)} aanpassen</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <ErrorDisplay error={error} title="Fout bij het laden van gegevens" />
                    </DialogBody>
                </DialogContainer>
            </Dialog>
        );
    }

    const canDelete = !trein.busEvacuatie && !trein.treinEvacuatie && contactpersonen.length === 0;

    function onSubmit(data: EditTreinFormData) {
        if (!form.formState.isDirty) {
            // shouldn't happen because of disabled button, but if it does, there are no changes so we close the dialog
            onOpenChange(false);
            return;
        }
        patchTrainMutation.mutate(createPatchBetrokkenTreinPayload(data, dossierNummer, String(trein.id)), {
            onSuccess: () => onOpenChange(false),
        });
    }

    function handleDelete() {
        // the BE handles delete logic: a soft delete (setting isRelevant to false) for betrokken and gestrande treinen. An actual delete for handmatige treinen.
        deleteTrainMutation.mutate(
            { dossierId: dossierNummer, treinId: trein.id },
            {
                onSuccess: () => setDeleteConfirmationDialogOpen(false),
            }
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer>
                <DialogHeader className="border-b pb-3">
                    <DialogTitle>Trein {BetrokkenTrein.formatTrainLabel(trein)} aanpassen</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                        <DialogBody>
                            {patchTrainMutation.error && <ErrorDisplay error={patchTrainMutation.error} />}
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-7">
                                    <SelectFormField
                                        form={form}
                                        label="Materieeltype"
                                        name="materieelAanduiding"
                                        optional
                                        options={getMaterieelTypeOptions(trein.materieel?.aanduiding)}
                                    />
                                </div>
                                <div className="col-span-5">
                                    <FormFieldInput form={form} name="materieelAantalBakken" label="Aantal bakken" optional />
                                </div>
                                {trein.type === "Handmatig" && (
                                    <div className="col-span-12">
                                        <FormFieldInput form={form} name="aantalReizigers" label="Reizigersaantal" optional />
                                    </div>
                                )}
                                {!canDelete && (
                                    <div className="col-span-12">
                                        <Helptext>
                                            Deze trein kun je niet verwijderen, omdat er medewerkers, een evacuatiebus of evacuatietrein aan
                                            gekoppeld zijn.
                                        </Helptext>
                                    </div>
                                )}
                            </div>
                        </DialogBody>
                        <DialogFooter>
                            {canDelete && (
                                <Button
                                    type="button"
                                    variant="tertiary"
                                    destructive
                                    className="mr-auto"
                                    onClick={() => setDeleteConfirmationDialogOpen(true)}
                                >
                                    Trein verwijderen
                                </Button>
                            )}

                            <Button
                                type="button"
                                variant="tertiary"
                                onClick={() => {
                                    onOpenChange(false);
                                }}
                            >
                                Annuleren
                            </Button>
                            <Button type="submit" variant="primary" disabled={patchTrainMutation.isPending || !form.formState.isDirty}>
                                Opslaan
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
                <ConfirmDialog
                    title="Trein verwijderen"
                    isOpen={deleteConfirmationDialogOpen}
                    onConfirm={handleDelete}
                    confirmLabel="Verwijderen"
                    isPending={deleteTrainMutation.isPending}
                    error={deleteTrainMutation.error}
                    onCancel={() => setDeleteConfirmationDialogOpen(false)}
                    destructive
                >
                    Weet je zeker dat je deze trein wil verwijderen?
                </ConfirmDialog>
            </DialogContainer>
        </Dialog>
    );
}

export default EditBetrokkenTreinDialog;
